// God → Gateway → Callbacks: raw callbacks received from ProtonPay (unsigned — status
// is only applied after a fresh checkStatus confirms it) and the signed callbacks
// SuperCash sends to merchant servers. "gateway/callbacks/merchants" opens the second view.
import { useState } from "react";
import { Clock, FileText, Radio, Webhook } from "lucide-react";
import { god } from "../../godApi";
import { useDebounced, usePagedList, usePolling, useSyncFilter } from "../../../components/sc/ui";
import {
  Callout, Cell, Chips, Code, Empty, Page, Pagination, Panel, PanelHero, Pill, Rows, SearchInput, Section, Segmented,
  Select, StatusPill, Table, Timeline, Toolbar, ago, shortId, useNav, when,
} from "../../kit";

const cbLabel = (c) => `${c.claimed_status || "?"} → ${c.verified_status || (c.kind === "return" ? "browser" : "unverified")}`;
const cbPill = (c) => <Pill tone={c.verified ? "green" : c.kind === "return" ? "gray" : "amber"} dot>{cbLabel(c)}</Pill>;

export default function GwCallbacks() {
  const { path, go } = useNav();
  const view = path === "gateway/callbacks/merchants" ? "merchants" : "upstream";
  return (
    <Page>
      <Segmented testIdPrefix="god-gw-cb" value={view}
        onChange={(v) => go(v === "merchants" ? "gateway/callbacks/merchants" : "gateway/callbacks")}
        options={[{ key: "upstream", label: "From ProtonPay" }, { key: "merchants", label: "To merchants" }]} />
      {view === "upstream" ? <Upstream /> : <Deliveries />}
      <style>{`
        .gwc-pad { padding: 12px 18px 0; }
        .gwc-type { text-transform: uppercase; }
      `}</style>
    </Page>
  );
}

// ─────────────────────────── from ProtonPay ───────────────────────────

function Upstream() {
  const [qInput, setQInput] = useState("");
  const q = useDebounced(qInput);
  const [open, setOpen] = useState(null);
  const list = usePagedList((p) => god.gwList("callbacks", p), { kind: "all", verified: "all", q: "" }, 50);
  useSyncFilter(list.setFilter, "q", q);
  usePolling(list.load, 4000, list.page === 1);
  const filtered = q || list.filters.kind !== "all" || list.filters.verified !== "all";

  return (
    <>
      <Section title="Callbacks from ProtonPay" icon={Radio} pad={false}
        sub="Raw and unsigned. A status is only applied after a fresh checkStatus confirms it.">
        <Toolbar>
          <SearchInput value={qInput} onChange={setQInput} placeholder="Exact ProtonPay id, ProtonPay order id or txn id" testId="god-gw-cb-search" />
          <Select aria-label="Type" value={list.filters.kind} onChange={(e) => list.setFilter("kind", e.target.value)}
            options={[["all", "All types"], ["payin", "Payin"], ["payout", "Payout"], ["return", "Return"]]} />
          <Select aria-label="Verification" value={list.filters.verified} onChange={(e) => list.setFilter("verified", e.target.value)}
            options={[["all", "Verified or not"], ["true", "Verified"], ["false", "Unverified"]]} />
        </Toolbar>
        {list.error && <div className="gwc-pad"><Callout tone="red" title="Couldn't load">{list.error}</Callout></div>}
        <Table testId="god-gw-callbacks" rows={list.data.items} loading={list.loading} onRowClick={setOpen} activeId={open?.id}
          empty={<Empty icon={Radio} title={filtered ? "No callbacks match" : "No callbacks received"}>
            {filtered ? "Try another type, status or id." : "ProtonPay posts here once payins or payouts change status."}
          </Empty>}
          columns={[
            { key: "at", label: "Received", render: (c) => <Cell main={when(c.received_at, true)} sub={ago(c.received_at)} /> },
            { key: "kind", label: "Type", render: (c) => <span className="k-mono gwc-type">{c.kind}</span> },
            { key: "merchant", label: "Merchant", hideSm: true, render: (c) => <Cell main={c.merchant_name || "—"} sub={c.note} /> },
            { key: "ext", label: "ProtonPay id", hideSm: true, render: (c) => <span className="k-mono">{shortId(c.external_id, 8) || "—"}</span> },
            { key: "ip", label: "From IP", hideSm: true, render: (c) => <span className="k-mono">{c.ip || "—"}</span> },
            { key: "status", label: "Sent → verified", render: cbPill },
          ]} />
        <Pagination page={list.data.page} pages={list.data.pages} total={list.data.total} size={list.size} onPage={list.setPage} onSize={list.setSize} />
      </Section>

      {open && (
        <Panel title={`${open.kind} callback`} sub={when(open.received_at, true)} onClose={() => setOpen(null)}>
          <PanelHero kicker={`From ProtonPay · ${open.kind}`} title={open.claimed_status || "No status"}
            meta={<>{cbPill(open)}<span>{ago(open.received_at)}</span></>} reference={open.external_id} />
          {open.note && <Callout tone="amber">{open.note}</Callout>}
          <Section title="Details" icon={FileText}>
            <Rows items={[
              ["Received", when(open.received_at, true)],
              ["From IP", open.ip, { mono: true }],
              ["Merchant", open.merchant_name],
              ["Claimed status", open.claimed_status],
              ["Verified status", open.verified_status],
              ["Verified", open.verified ? <Pill tone="green">Yes</Pill> : <Pill tone={open.kind === "return" ? "gray" : "amber"}>No</Pill>],
              ["ProtonPay id", open.external_id, { mono: true, copy: true }],
              ["ProtonPay order id", open.merchant_order_id, { mono: true, copy: true }],
              ["Txn", open.ref_id, { mono: true, copy: true }],
            ]} />
          </Section>
          <Section title="Payload" icon={FileText}>
            <Code value={open.payload} />
          </Section>
        </Panel>
      )}
    </>
  );
}

// ─────────────────────────── to merchants ───────────────────────────

const DELIVERY_STATUSES = [["all", "All"], ["delivered", "Delivered"], ["retrying", "Retrying"], ["failed", "Failed"], ["pending", "Pending"], ["no_url", "No URL"]];

function Deliveries() {
  const [open, setOpen] = useState(null);
  const list = usePagedList((p) => god.gwList("deliveries", p), { status: "all" }, 50);
  usePolling(list.load, 5000, list.page === 1);

  return (
    <>
      <Section title="Callbacks to merchants" icon={Webhook} pad={false} sub="Signed events SuperCash sends to merchant servers.">
        <Toolbar>
          <Chips options={DELIVERY_STATUSES.map(([key, label]) => ({ key, label }))} value={list.filters.status} onChange={(v) => list.setFilter("status", v)} />
        </Toolbar>
        {list.error && <div className="gwc-pad"><Callout tone="red" title="Couldn't load">{list.error}</Callout></div>}
        <Table testId="god-gw-deliveries" rows={list.data.items} loading={list.loading} onRowClick={setOpen} activeId={open?.id}
          empty={<Empty icon={Webhook} title={list.filters.status !== "all" ? "Nothing with this status" : "No callbacks sent yet"}>
            {list.filters.status !== "all" ? "Try another status." : "Merchants get a callback whenever a payin or payout changes status."}
          </Empty>}
          columns={[
            { key: "at", label: "Created", hideSm: true, render: (d) => <Cell main={when(d.created_at, true)} sub={ago(d.created_at)} /> },
            { key: "merchant", label: "Merchant", render: (d) => <Cell main={d.merchant_name || "—"} sub={d.order_ref} /> },
            { key: "event", label: "Event", render: (d) => <span className="k-mono">{d.event}</span> },
            { key: "url", label: "URL", hideSm: true, render: (d) => <Cell mono main={d.url || "—"} sub={d.last_error} /> },
            { key: "try", label: "Tries", align: "right", render: (d) => <span className="k-mono">{d.attempts} · {d.last_status_code || "—"}</span> },
            { key: "status", label: "Status", render: (d) => <StatusPill status={d.status} /> },
          ]} />
        <Pagination page={list.data.page} pages={list.data.pages} total={list.data.total} size={list.size} onPage={list.setPage} onSize={list.setSize} />
      </Section>

      {open && (
        <Panel title={open.event} sub={open.merchant_name} onClose={() => setOpen(null)} width={600}>
          <PanelHero kicker={`To ${open.merchant_name || "merchant"}`} title={open.event}
            meta={<><StatusPill status={open.status} /><span>{open.attempts} attempt(s)</span></>} reference={open.url || undefined} />
          {open.last_error && <Callout tone={open.status === "delivered" ? "amber" : "red"} title="Last error">{open.last_error}</Callout>}
          <Section title="Details" icon={FileText}>
            <Rows items={[
              ["Order id", open.order_ref, { mono: true, copy: true }],
              ["Transaction", open.txn_id ? `${open.txn_kind || ""} ${open.txn_id}`.trim() : null, { mono: true, copy: open.txn_id }],
              ["URL", open.url || "No URL configured", { mono: true, copy: open.url }],
              ["Attempts", open.attempts],
              ["Last HTTP status", open.last_status_code, { mono: true }],
              ["Created", when(open.created_at, true)],
              ["Last attempt", open.last_attempt_at ? when(open.last_attempt_at, true) : null],
              ["Next attempt", open.status === "retrying" && open.next_attempt_at ? when(open.next_attempt_at, true) : null],
              ["Delivered", open.delivered_at ? when(open.delivered_at, true) : null],
              ["Delivery id", open.id, { mono: true, copy: true }],
            ]} />
          </Section>
          <Section title="Attempts" icon={Clock}>
            <Timeline events={(open.history || []).map((h) => ({
              at: h.at,
              message: h.error
                ? `${h.error}${h.latency_ms != null ? ` · ${h.latency_ms} ms` : ""}`
                : `HTTP ${h.status_code}${h.latency_ms != null ? ` · ${h.latency_ms} ms` : ""}`,
            }))} />
          </Section>
          <Section title="Payload" icon={FileText}>
            <Code value={open.payload} />
          </Section>
          <Section title="Raw record" icon={FileText}>
            <Code value={open} />
          </Section>
        </Panel>
      )}
    </>
  );
}
